import React, { useEffect, useRef, useState } from "react";
import { usePlayer } from "@/context/PlayerContext";

const BAR_COUNT = 32;

export default function Equalizer() {
  const { getAnalyser, getDataArray, playing } = usePlayer();
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      const analyser = getAnalyser();
      const data = getDataArray();

      let values = new Array(BAR_COUNT).fill(0);
      if (analyser && data && playing) {
        analyser.getByteFrequencyData(data);
        const step = Math.floor(data.length / BAR_COUNT) || 1;
        for (let i = 0; i < BAR_COUNT; i++) {
          let sum = 0;
          for (let j = 0; j < step; j++) sum += data[i * step + j] || 0;
          values[i] = sum / step / 255;
        }
      } else if (playing) {
        // Fallback pseudo animation
        const t = Date.now() / 200;
        for (let i = 0; i < BAR_COUNT; i++) {
          values[i] = 0.15 + Math.abs(Math.sin(t + i * 0.4)) * 0.5;
        }
      }

      const gap = 2;
      const bw = (w - gap * (BAR_COUNT - 1)) / BAR_COUNT;
      for (let i = 0; i < BAR_COUNT; i++) {
        const v = values[i];
        const bh = Math.max(2, v * h);
        const x = i * (bw + gap);
        const y = h - bh;
        // Color gradient across spectrum: cyan → pink → yellow
        const ratio = i / BAR_COUNT;
        let color;
        if (ratio < 0.5) {
          const local = ratio / 0.5;
          color = `rgba(${Math.round(6 + local * (236 - 6))},${Math.round(182 - local * (182 - 72))},${Math.round(212 - local * (212 - 153))},${0.55 + v * 0.45})`;
        } else {
          const local = (ratio - 0.5) / 0.5;
          color = `rgba(${Math.round(236 - local * (236 - 234))},${Math.round(72 + local * (179 - 72))},${Math.round(153 - local * 153)},${0.55 + v * 0.45})`;
        }
        ctx.fillStyle = color;
        ctx.fillRect(x, y, bw, bh);
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    const handleResize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    rafRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", handleResize);
    };
  }, [getAnalyser, getDataArray, playing]);

  return (
    <canvas
      data-testid="equalizer-canvas"
      ref={canvasRef}
      className="w-full h-8 block"
      style={{ width: "100%", height: 32 }}
    />
  );
}
